import type { Recipe } from '../types';

// One pot, one hour, two completely different soups. The base simmers together
// and then gets divided in half at step 5 — one half goes Tuscan, the other
// goes green chile. Nobody eats the same lunch four days running.

export const splitPotChickenWhiteBeanSoup: Recipe = {
  slug: 'split-pot-chicken-white-bean-soup',
  title: 'Split-Pot Chicken & White Bean Soup',
  meal: 'lunch',
  category: 'meal',
  types: ['soup'],
  cuisines: ['italian', 'mexican'],
  traits: ['freezer-friendly', 'one-pot', 'two-in-one', 'make-ahead'],
  yield: '12 servings — 6 Tuscan, 6 green chile',
  activeTime: '30 min',
  totalTime: '1 hr 15 min',
  freezerLife: '4–6 months',
  difficulty: 1,

  summary:
    'A chicken and white bean base that gets split down the middle at the end and finished two ways — spinach and parmesan on one side, green chile and lime on the other. Same hour of work, two soups nobody gets sick of.',

  flavorNotes:
    'The base on its own is deliberately plain — chicken, beans, mirepoix, thyme. Do not judge it at step 4; it is supposed to taste like it is waiting for something, because it is. The Tuscan half lands rich and savory with a lemon lift at the end, and the beans break down enough to make it feel creamy before any cream goes in. The green chile half goes bright and earthy, warm rather than hot, and gets its edge from lime rather than heat. Both should taste finished, not like a base with things floating in it, which is what the last two minutes of each finish are for.',

  ingredients: [
    {
      id: 'chicken',
      name: 'Boneless chicken thighs',
      total: '3 lb',
      prep: 'thighs beat breast here — they do not dry out over a long simmer',
      uses: [{ step: 1, amount: '3 lb' }],
    },
    {
      id: 'olive-oil',
      name: 'Olive oil',
      total: '2 tbsp',
      uses: [{ step: 1, amount: '2 tbsp' }],
    },
    {
      id: 'onion',
      name: 'Yellow onion',
      total: '2 large',
      prep: 'diced small',
      uses: [{ step: 2, amount: '2 large' }],
    },
    {
      id: 'carrots',
      name: 'Carrots',
      total: '4 medium',
      prep: 'diced small',
      uses: [{ step: 2, amount: '4 medium' }],
    },
    {
      id: 'celery',
      name: 'Celery',
      total: '4 ribs',
      prep: 'diced small',
      uses: [{ step: 2, amount: '4 ribs' }],
    },
    {
      id: 'garlic',
      name: 'Minced garlic',
      total: '2 tbsp',
      uses: [{ step: 2, amount: '2 tbsp', note: 'last 30 seconds' }],
    },
    {
      id: 'broth',
      name: 'Chicken broth',
      total: '12 cups',
      prep: 'Better Than Bouillon chicken base, 1 tsp per cup of hot water',
      fromFreezer: true,
      uses: [{ step: 3, amount: '12 cups' }],
    },
    {
      id: 'white-beans',
      name: 'Cannellini or great northern beans, 15.5 oz',
      total: '4 cans',
      prep: 'drained and rinsed',
      uses: [{ step: 3, amount: '4 cans' }],
    },
    {
      id: 'thyme',
      name: 'Dried thyme',
      total: '2 tsp',
      uses: [{ step: 3, amount: '2 tsp' }],
    },
    {
      id: 'bay',
      name: 'Bay leaves',
      total: '3',
      uses: [{ step: 3, amount: '3', note: 'fished out at step 4' }],
    },
    {
      id: 'spinach',
      name: 'Fresh spinach',
      total: '5 oz',
      uses: [{ step: 6, amount: '5 oz', note: 'Tuscan half' }],
    },
    {
      id: 'parmesan',
      name: 'BelGioioso Parmesan',
      total: '1 cup',
      prep: 'grated off the block',
      uses: [{ step: 6, amount: '1 cup', note: 'Tuscan half' }],
    },
    {
      id: 'lemon',
      name: 'Lemon',
      total: '1',
      prep: 'juiced',
      uses: [{ step: 6, amount: '1', note: 'Tuscan half, off the heat' }],
    },
    {
      id: 'green-chiles',
      name: 'Diced green chiles, 4 oz cans',
      total: '2 cans',
      uses: [{ step: 7, amount: '2 cans', note: 'green chile half' }],
    },
    {
      id: 'cumin',
      name: 'Ground cumin',
      total: '1 tbsp',
      uses: [{ step: 7, amount: '1 tbsp', note: 'green chile half' }],
    },
    {
      id: 'corn',
      name: 'Frozen corn',
      total: '2 cups',
      uses: [{ step: 7, amount: '2 cups', note: 'green chile half' }],
    },
    {
      id: 'lime',
      name: 'Limes',
      total: '2',
      prep: 'juiced',
      uses: [{ step: 7, amount: '2', note: 'green chile half, off the heat' }],
    },
    {
      id: 'cilantro',
      name: 'Cilantro',
      total: '1 bunch',
      prep: 'chopped',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy:
        'At the table only. It goes slimy in the freezer and loses everything within a minute of hitting hot soup.',
      uses: [{ step: 7, amount: '1 bunch', note: 'green chile half, at the table' }],
    },
    {
      id: 'cream',
      name: 'Heavy cream',
      total: '½ cup',
      optional: true,
      optionalKind: 'at-reheat',
      optionalWhy:
        'Held back on purpose. Dairy splits in the freezer and comes back grainy — stir it in as you reheat instead.',
      uses: [{ step: 8, amount: '½ cup', note: 'Tuscan half — added at REHEAT, never frozen' }],
    },
    {
      id: 'salt',
      name: 'Salt and pepper',
      total: 'to taste',
      uses: [{ step: 4, amount: 'to taste' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Brown the chicken',
      time: '10 min',
      ingredientIds: ['chicken', 'olive-oil'],
      body:
        'Heat the {2 tablespoons|olive-oil} of olive oil in a large pot over medium-high. Season the {3 lb|chicken} of chicken thighs with salt and pepper and brown them in two batches, about 3 minutes a side. They do not need to cook through — they finish in the broth. Move them to a plate.',
      tip:
        'The browning is the only real flavor this base gets before the finishes go in. Skipping it gives you soup that tastes like hot broth with things in it.',
    },
    {
      n: 2,
      title: 'Sweat the mirepoix',
      time: '8 min',
      ingredientIds: ['onion', 'carrots', 'celery', 'garlic'],
      body:
        'Into the same pot with the chicken fat still in it: the onion, carrots and celery — the mirepoix. Sweat them over medium heat for 7 to 8 minutes, until the onion goes translucent and the whole thing smells sweet. Scrape the browned bits off the bottom as they release. Add the {2 tablespoons|garlic} of garlic for the last 30 seconds.',
    },
    {
      n: 3,
      title: 'Build the pot and simmer',
      time: '30 min',
      ingredientIds: ['broth', 'white-beans', 'thyme', 'bay'],
      body:
        'Return the chicken and any juices on the plate. Add the {12 cups|broth} of chicken broth, the {4 cans|white-beans} of drained beans, {2 tsp|thyme} of thyme and the bay leaves. Bring it up, then drop to a low simmer, uncovered, for 30 minutes.',
    },
    {
      n: 4,
      title: 'Shred the chicken and season the base',
      time: '10 min',
      ingredientIds: ['salt'],
      body:
        'Fish out the bay leaves and pull the chicken onto a board. Shred it with two forks and return it to the pot. Now salt the base until it tastes like something on its own — this is the last moment it is one pot, and getting the salt right here means both halves start from the same good place.',
      tip:
        'Season the base properly even though it tastes plain. Both finishes assume a base that is already correctly salted; fixing it later in two separate pots is twice the work and never comes out even.',
    },
    {
      n: 5,
      title: 'Split the pot',
      time: '2 min',
      critical: true,
      ingredientIds: [],
      body:
        'Ladle half the soup into a second pot. Eyeball it — this does not need to be precise. One half becomes step 6, the other becomes step 7, and from here they never touch each other again.',
      tip:
        'If you would rather have six servings of one soup this week and six of the other next month, freeze half the base unfinished right here and do the finish after it thaws. It costs nothing and both finishes take four minutes.',
    },
    {
      n: 6,
      title: 'Finish half Tuscan',
      time: '4 min',
      ingredientIds: ['spinach', 'parmesan', 'lemon'],
      body:
        'Bring the first pot back to a gentle simmer. Stir in the {5 oz|spinach} of spinach a handful at a time and let it collapse, about 2 minutes. Off the heat, stir in the {1 cup|parmesan} of grated Parmesan until it melts through, then the juice of the lemon. Taste and adjust the salt.',
      tip:
        'Parmesan goes in off the heat. Boiled hard cheese turns stringy and separates into little rubbery threads that never come back together.',
    },
    {
      n: 7,
      title: 'Finish half green chile',
      time: '4 min',
      ingredientIds: ['green-chiles', 'cumin', 'corn', 'lime', 'cilantro'],
      body:
        'Bring the second pot to a gentle simmer. Stir in the {2 cans|green-chiles} of green chiles, {1 tablespoon|cumin} of cumin and the {2 cups|corn} of frozen corn, and give it 3 to 4 minutes for the corn to heat through and the cumin to open up. Off the heat, the juice of the limes. Cilantro goes over individual bowls at the table, not into the pot.',
      tip:
        'Cilantro loses everything within about a minute of hitting hot soup, and it goes slimy in the freezer. Keep it at the table.',
    },
    {
      n: 8,
      title: 'Cool and pack separately',
      time: '30 min, mostly waiting',
      ingredientIds: ['cream'],
      body:
        'Cool both pots completely before anything gets bagged — about 30 minutes, faster if you spread them into shallow pans. Label the bags, because once these are frozen solid they look identical. If you want the Tuscan half creamy, the {½ cup|cream} of heavy cream goes in when you reheat it, never before it freezes.',
      tip:
        'Cream and dairy split when frozen and thawed — it comes back grainy and curdled-looking no matter how gently you warm it. Freeze the soup plain and add the cream in the last minute of reheating.',
    },
  ],

  freezing:
    'Quart bags, flat, air pressed out, 4–6 months either way. Label which is which — frozen Tuscan and frozen green chile are indistinguishable through a freezer bag at seven in the morning. The Tuscan half freezes without cream; add that on reheat. Both halves thicken noticeably in the freezer as the beans keep breaking down, so expect to loosen them with a splash of broth or water.',

  reheat: [
    {
      method: 'Stovetop',
      best: true,
      detail:
        'Thaw overnight, then medium-low with a splash of broth to loosen it, about 8 minutes. Stir the cream into the Tuscan half in the last minute off the heat. The green chile half wants a fresh squeeze of lime at the end — the frozen lime juice goes flat.',
    },
    {
      method: 'Microwave',
      detail:
        'Thawed, covered, 70% power in 2-minute rounds, stirring between. Fine for one bowl at the office; the spinach goes a little limp but the flavor holds.',
    },
    {
      method: 'From frozen',
      detail:
        'Run the bag under warm water to release the block, into a covered saucepan on low with ¼ cup of water, about 15 minutes, breaking it up as it loosens. Stir often — the beans on the bottom scorch if you walk away.',
    },
  ],

  glossary: [
    {
      term: 'mirepoix',
      plain:
        'Onion, carrot and celery diced small and cooked down together — roughly two parts onion to one part each of the others. It is the flavor foundation under most soups and stews in Western cooking. Nobody eating it thinks about it; leave it out and the soup tastes thin and you cannot say why.',
    },
    {
      term: 'sweat',
      plain:
        'Cooking vegetables over medium or lower heat until they go soft and translucent without browning. You want them to release their water and turn sweet, not to caramelize. If they are sizzling hard or picking up color, the heat is too high.',
    },
    {
      term: 'shred',
      plain:
        'Pull cooked meat apart along its grain with two forks, or with your hands once it is cool enough. Meat shreds easily only when it is actually done — if it fights you, it needs more time in the pot, not more force.',
    },
  ],

  inspiration: [
    { label: 'Blue Jean Chef — Tuscan chicken and white bean soup', url: 'https://bluejeanchef.com/recipes/tuscan-bean-soup-with-chicken/' },
    { label: 'How Sweet Eats — white bean soup with spinach and parmesan', url: 'https://www.howsweeteats.com/2023/03/fire-roasted-white-bean-soup/' },
    { label: 'Souper Cubes — make-ahead freezer meal prep', url: 'https://www.soupercubes.com/blogs/blog/make-ahead-meal-prep' },
  ],

  whereWeVeered:
    'Most Tuscan white bean soups are built as a single finished pot. We stopped one step short and split the base instead, which is the only structural difference and the whole reason this earns its hour — two distinct lunches out of one session of chopping. We used thighs rather than breast because a 30-minute simmer dries breast out, kept the cream out of the freezer entirely, and moved both acids, the lemon and the lime, to the very end off the heat where they still taste like something.',
};
